import React from 'react';
import { arrayOf, node, object, shape, string } from 'prop-types';

import ListingCard from '../../../components/ListingCard/ListingCard';
import SectionHead from './SectionHead';
import LandingLink from './LandingLink';

/**
 * The listing grid on a landing page.
 *
 * Uses the app's own ListingCard rather than bespoke markup. That was the whole
 * point of moving these pages into the app: a hand-rolled card is the same
 * drift risk the hand-rolled header was. It also means these listings get
 * everything the rest of the site gives them - the wishlist button, the
 * "Empfohlen" badge, availability, responsive images, correct price and
 * currency handling - instead of a static approximation that silently falls
 * behind.
 *
 * The data comes from the shared listing-collections duck and is fetched in
 * loadData, so the cards are server-rendered and ship as crawlable links.
 *
 * @param {Object} props
 * @param {Array} props.listings denormalised listings
 * @param {string} props.heading section heading
 * @param {string} [props.text] section lead
 * @param {React.Node} [props.emptyState] rendered instead of the grid when there
 *   are no listings - never an empty grid
 * @param {Object} [props.footerLink] { link, label }
 */
const ListingGrid = props => {
  const { listings, heading, text, emptyState, footerLink } = props;
  const hasListings = listings.length > 0;

  return (
    <section className="section-listings" aria-labelledby="listings-title">
      <div className="container">
        <SectionHead id="listings-title" heading={heading} text={text} />

        {hasListings ? (
          <div className="listings-grid">
            {listings.map(listing => (
              <ListingCard
                key={listing.id.uuid}
                listing={listing}
                renderSizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 300px"
              />
            ))}
          </div>
        ) : (
          emptyState
        )}

        {footerLink && hasListings ? (
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <LandingLink className="btn btn-ghost" link={footerLink.link}>
              {footerLink.label}
            </LandingLink>
          </div>
        ) : null}
      </div>
    </section>
  );
};

ListingGrid.propTypes = {
  listings: arrayOf(object),
  heading: string.isRequired,
  text: string,
  emptyState: node,
  footerLink: shape({ link: object, label: string }),
};
ListingGrid.defaultProps = { listings: [] };

export default ListingGrid;
